import { cn } from '../cn.ts';
import type { MockKanbanColumn } from './types.ts';

export interface MockKanbanProps {
  readonly columns: readonly MockKanbanColumn[];
  /**
   * Hai mật độ ĐO ĐƯỢC từ thiết kế, không phải hai gu thẩm mỹ:
   * `hero` = bảng trong mock app (4 cột, gap 10, card pad 9/10 — `template.html:723`)
   * `section` = mock `#pm` (5 cột, gap 12, cột pad 12 — `:871`)
   */
  readonly density?: 'hero' | 'section';
  readonly className?: string;
}

/**
 * Bảng kanban của mock — component DUY NHẤT có hai hộ dùng với hai cấu hình khác nhau, và là lý
 * do lô A gộp với lô B thành A′: chốt API ở đây bằng một hộ dùng rồi sửa sau nghĩa là sửa
 * `packages/ui` giữa lô.
 *
 * Dưới `md` **cuộn ngang có `scroll-snap`**, KHÔNG xếp thành 1 cột (`reference.md` §2.1/§2.2):
 * kanban 1 cột không còn là kanban — nó mất đúng thứ đang chứng minh. Mỗi cột `min-w-45` (180px)
 * để cột kế tiếp lấp ló ở mép phải, tức người dùng thấy được là còn cuộn được.
 *
 * `overscroll-x-contain` chặn cú cuộn ở cuối bảng lan ra thành cử chỉ "back" của trình duyệt.
 */
export function MockKanban({ columns, density = 'section', className }: MockKanbanProps) {
  const isHero = density === 'hero';

  return (
    <div
      className={cn(
        'flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain',
        isHero ? 'gap-2.5' : 'gap-3',
        'md:grid md:snap-none md:overflow-visible',
        isHero ? 'md:grid-cols-4' : 'md:grid-cols-5',
        className,
      )}
    >
      {columns.map((column) => (
        <div
          key={column.title}
          className={cn(
            'flex min-w-45 shrink-0 snap-start flex-col gap-2 rounded-card-sm bg-ink-100 md:min-w-0',
            isHero ? 'p-2.5' : 'p-3',
          )}
        >
          <div className="text-micro leading-[normal] font-bold tracking-label text-ink-600">
            {column.title}
          </div>

          {column.cards.map((card) => (
            <div
              key={card.text}
              className={cn(
                'rounded-chip bg-surface p-2.5 text-caption leading-[normal]',
                'transition [transition-duration:var(--duration-hover)]',
                card.done === true ? 'text-ink-400 line-through' : 'text-ink-mock-900',
                card.active === true &&
                  cn(
                    'border border-brand-200',
                    isHero ? 'shadow-card-active-hero' : 'shadow-card-active',
                  ),
                // Hover chỉ cho card CHƯA chọn và CHƯA xong — dùng lại đúng nhánh này để bóng
                // hover không bao giờ sống cùng bóng của trạng thái đang chọn.
                card.active !== true &&
                  card.done !== true &&
                  cn(
                    'shadow-hairline',
                    isHero ? 'hover:shadow-card-active-hero' : 'hover:shadow-card-active',
                  ),
              )}
            >
              {card.text}

              {(card.assignee !== undefined || card.priority !== undefined) && (
                <div className="mt-[7px] flex items-center gap-[5px]">
                  {card.assignee !== undefined && (
                    <span className="grid size-[18px] place-items-center rounded-pill bg-brand-50 text-nano font-bold text-brand-600">
                      {card.assignee}
                    </span>
                  )}
                  {card.priority !== undefined && (
                    <span className="rounded-pill bg-warn-bg px-1.5 py-px text-nano font-bold text-warn-fg">
                      {card.priority}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
